// client/src/hooks/useDataSharingConsent.ts
//
// Hook React para manejar el opt-in/opt-out de compartir stats anónimas.
//
// grant() — otorga consent. Futuros imports se subirán a player_stats.
// revoke(deleteHistory) — revoca consent. Si deleteHistory=true, también
//                         borra todos los player_stats históricos del user.

import { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';

export const CURRENT_CONSENT_VERSION = '1.0.0';

export interface DataSharingConsentState {
  consent: boolean | null;
  loading: boolean;
  updating: boolean;
  error: string | null;
  grant: () => Promise<boolean>;
  /**
   * Revocar el consent.
   * @param deleteHistory - Si true, también borra los player_stats históricos
   *                        del user. El consent_log siempre se preserva
   *                        (append-only audit trail por compliance).
   */
  revoke: (deleteHistory?: boolean) => Promise<boolean>;
}

export function useDataSharingConsent(user: User | null): DataSharingConsentState {
  const [consent, setConsent] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!SUPABASE_ENABLED || !user) {
      setConsent(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('data_sharing_consent')
          .eq('id', user.id)
          .single();
        if (cancelled) return;
        if (fetchError) {
          console.warn('[useDataSharingConsent] fetch:', fetchError.message);
          setConsent(false);
        } else {
          setConsent(data?.data_sharing_consent ?? false);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('[useDataSharingConsent] unexpected:', err);
        setConsent(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const writeConsent = useCallback(
    async (action: 'granted' | 'revoked', deleteHistory: boolean): Promise<boolean> => {
      if (!SUPABASE_ENABLED || !user) {
        setError('Not authenticated');
        return false;
      }

      setUpdating(true);
      setError(null);

      const newValue = action === 'granted';

      try {
        // 1. Update profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            data_sharing_consent: newValue,
            data_sharing_consent_at: new Date().toISOString(),
            data_sharing_consent_version: CURRENT_CONSENT_VERSION,
          })
          .eq('id', user.id);

        if (updateError) {
          throw new Error(`Profile update failed: ${updateError.message}`);
        }

        // 2. Insert audit log row (best-effort, non-blocking)
        const { error: logError } = await supabase.from('consent_log').insert({
          user_id: user.id,
          action,
          version: CURRENT_CONSENT_VERSION,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        });
        if (logError) {
          console.warn('[useDataSharingConsent] consent_log write failed:', logError.message);
        }

        // 3. If revoking + deleteHistory, wipe historical player_stats.
        //    Note: the consent_log is NEVER deleted — it's the audit trail.
        if (action === 'revoked' && deleteHistory) {
          const { error: deleteError } = await supabase
            .from('player_stats')
            .delete()
            .eq('user_id', user.id);

          if (deleteError) {
            // Non-fatal: consent is already revoked, but we couldn't delete.
            // The user should be informed but we don't want to block.
            console.error(
              '[useDataSharingConsent] delete history failed:',
              deleteError.message
            );
            setError(
              `Consent revoked but historical data could not be deleted: ${deleteError.message}`
            );
          }
        }

        setConsent(newValue);
        return true;
      } catch (err: any) {
        console.error('[useDataSharingConsent] update error:', err);
        setError(err.message || 'Unknown error');
        return false;
      } finally {
        setUpdating(false);
      }
    },
    [user]
  );

  const grant = useCallback(() => writeConsent('granted', false), [writeConsent]);
  const revoke = useCallback(
    (deleteHistory: boolean = false) => writeConsent('revoked', deleteHistory),
    [writeConsent]
  );

  return { consent, loading, updating, error, grant, revoke };
}
